import React, { useState, useEffect } from 'react';
import {
    TextField,
    Button,
    MenuItem,
    Grid,
    Box,
    Typography,
    Card,
    CardContent,
    IconButton,
    Table,
    TableContainer,
    TableRow,
    TableCell,
    TableBody,
    Select,
    FormControl,
    Paper,
    useTheme,
    CardHeader,
    Tooltip,
    Chip,
    Divider,
    Badge,
    Collapse,
    TableHead,
    CircularProgress,
    Alert
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business'; // for JnP
import StorefrontIcon from '@mui/icons-material/Storefront'; // for Bench Sales
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'; // for Both
import { alpha } from '@mui/material/styles';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    ShoppingCart as CartIcon,
    Visibility as VisibilityIcon,
    Description as DescriptionIcon,
    DateRange as DateIcon,
    Person as PersonIcon,
    CheckCircle as ApprovedIcon,
    AttachMoney as PriceIcon,
    Category as CategoryIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Styled Components
const StyledCard = styled(Card)(({ theme }) => ({
    borderRadius: '12px',
    boxShadow: '0 4px 20px 0 rgba(0, 0, 0, 0.05)',
    border: '1px solid rgba(230, 230, 230, 0.5)',
    transition: 'all 0.3s ease',
    '&:hover': {
        boxShadow: '0 8px 24px 0 rgba(0, 0, 0, 0.08)'
    }
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    '& .MuiTableHead-root': {
        '& .MuiTableCell-root': {
            backgroundColor: theme.palette.grey[50],
            fontWeight: 600,
            color: theme.palette.text.secondary,
            borderBottom: `2px solid ${theme.palette.divider}`
        }
    },
    '& .MuiTableRow-root:hover': {
        backgroundColor: alpha(theme.palette.primary.light, 0.04)
    }
}));

const StyledButton = styled(Button)(({ theme }) => ({
    borderRadius: '8px',
    textTransform: 'none',
    boxShadow: 'none',
    fontWeight: 500,
    transition: 'all 0.2s ease',
    '&:hover': {
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
    }
}));

const ActionIconButton = styled(IconButton)(({ theme }) => ({
    padding: '6px',
    marginLeft: '4px',
    borderRadius: '6px'
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
    borderRadius: '16px',
    fontWeight: 500,
    fontSize: '0.75rem',
    backgroundColor: status === 'active'
        ? alpha(theme.palette.success.main, 0.1)
        : alpha(theme.palette.error.main, 0.1),
    color: status === 'active'
        ? theme.palette.success.dark
        : theme.palette.error.dark,
    border: status === 'active'
        ? `1px solid ${alpha(theme.palette.success.main, 0.2)}`
        : `1px solid ${alpha(theme.palette.error.main, 0.2)}`
}));

const PriceBadge = styled(Badge)(({ theme }) => ({
    '& .MuiBadge-badge': {
        right: -10,
        top: 13,
        padding: '0 4px',
        backgroundColor: theme.palette.success.main,
        color: theme.palette.success.contrastText
    },
}));

const Prices = () => {
    // ...existing code...
    // All useState hooks above
    const [formData, setFormData] = useState({
        id: null,
        priceListName: '',
        priceListStatus: 'active',
        category: '',
        createdBy: 'Admin User',
        approvedBy: 'Finance Team',
        creationDate: new Date().toISOString().split('T')[0],
        description: ''
    });
    const theme = useTheme();
    useEffect(() => {
        const fetchItemsByCategory = async () => {
            if (!formData.category) {
                setItemOptions([]);
                return;
            }
            setIsItemsLoading(true);
            try {
                const itemsResponse = await fetch(`${process.env.REACT_APP_API_URL}/jnp_service_items`);
                if (itemsResponse.ok) {
                    const itemsData = await itemsResponse.json();
                    // Filter items by selected category
                    const filteredItems = itemsData.filter(item => item.category === formData.category);
                    const transformedItems = filteredItems.map(item => ({
                        value: item.id,
                        label: item.item_name,
                        description: item.item_description
                    }));
                    setItemOptions(transformedItems);
                } else {
                    setItemOptions([]);
                }
            } catch (err) {
                setItemOptions([]);
            } finally {
                setIsItemsLoading(false);
            }
        };
        fetchItemsByCategory();
    }, [formData.category]);
    const CATEGORY_OPTIONS = [
        {
            value: 'jnp',
            label: 'JnP',
            icon: <BusinessIcon sx={{ color: '#1976d2' }} />
        },
        {
            value: 'bench_sales',
            label: 'Bench Sales',
            icon: <StorefrontIcon sx={{ color: '#388e3c' }} />
        },
        // {
        //     value: 'both',
        //     label: 'Both',
        //     icon: <CompareArrowsIcon sx={{ color: '#f57c00' }} />
        // }
    ];
    const [items, setItems] = useState([]);
    const [newItem, setNewItem] = useState({ name: '', price: '', description: '' });
    const [isAddingItem, setIsAddingItem] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editData, setEditData] = useState({});
    const [savedPriceLists, setSavedPriceLists] = useState([]);
    const [expandedPriceList, setExpandedPriceList] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isItemsLoading, setIsItemsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [ITEM_OPTIONS, setItemOptions] = useState([]);

    const PRICE_LIST_STATUS = [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' }
    ];

    // Fetch all price lists on component mount
    useEffect(() => {
        const fetchPriceLists = async () => {
            setIsLoading(true);
            try {
                // First get the basic price lists data
                const response = await fetch(`${process.env.REACT_APP_API_URL}/price-lists`);
                if (!response.ok) {
                    throw new Error('Failed to fetch price lists');
                }
                const data = await response.json();

                // Next, fetch service items if they haven't been fetched yet
                if (ITEM_OPTIONS.length === 0) {
                    const itemsResponse = await fetch(`${process.env.REACT_APP_API_URL}/jnp_service_items?category=${formData.category}`);
                    if (itemsResponse.ok) {
                        const itemsData = await itemsResponse.json();
                        const transformedItems = itemsData.map(item => ({
                            value: item.id,
                            label: item.item_name,
                            description: item.item_description
                        }));
                        setItemOptions(transformedItems);
                    }
                }
                
                // Then fetch the full details for each price list
                const listsWithItems = await Promise.all(data.map(async (list) => {
                    const detailResponse = await fetch(`${process.env.REACT_APP_API_URL}/price-lists/${list.id}`);
                    if (!detailResponse.ok) {
                        throw new Error(`Failed to fetch items for price list ${list.id}`);
                    }
                    const detailData = await detailResponse.json();
                    return {
                        ...list,
                        items: detailData.items || [],
                        item_count: detailData.items?.length || 0
                    };
                }));
                
                setSavedPriceLists(listsWithItems);
            } catch (err) {
                setError(err.message);
                console.error('Error fetching price lists:', err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchPriceLists();
    }, []);

    const getItemNameById = (itemId) => {
        if (!itemId) return 'Unknown Item';

        // First try to find by value (ID)
        const itemById = ITEM_OPTIONS.find(option =>
            option.value?.toString() === itemId?.toString()
        );

        // If not found by ID, try to find by label (name)
        const itemByName = ITEM_OPTIONS.find(option =>
            option.label?.toString() === itemId?.toString()
        );

        return itemById?.label || itemByName?.label || 'Unknown Item';
    };
    const getItemDescriptionById = (itemId) => {
        const item = ITEM_OPTIONS.find(option => option.value === itemId);
        return item ? item.description : '';
    };

    const getAvailableItems = () => {
        return ITEM_OPTIONS.filter(item =>
            !items.some(listItem =>
                listItem.name?.toString() === item.value?.toString()
            ) ||
            (editingId && items.find(i => i.id === editingId)?.name?.toString() === item.value?.toString())
        );
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleAddItemClick = () => {
        setIsAddingItem(true);
        setNewItem({ name: '', price: '', description: '' });
    };

    const handleNewItemChange = (e) => {
        const { name, value } = e.target;

        if (name === 'name') {
            const selectedItem = ITEM_OPTIONS.find(item => item.value?.toString() === value?.toString());
            if (selectedItem) {
                setNewItem(prev => ({
                    ...prev,
                    name: value,
                    description: selectedItem.description || '',
                    price: prev.price // Keep the existing price if any
                }));
                return;
            }
        }

        setNewItem(prev => ({ ...prev, [name]: value }));
    };

    const handleSaveNewItem = () => {
        if (!newItem.name) {
            setError('Item name is required');
            return;
        }
        if (!newItem.price || isNaN(newItem.price) || parseFloat(newItem.price) <= 0) {
            setError('Please enter a valid price');
            return;
        }

        const selectedItem = ITEM_OPTIONS.find(item => item.value?.toString() === newItem.name?.toString());

        const itemToAdd = {
            id: Date.now(),
            name: newItem.name,
            price: parseFloat(newItem.price).toFixed(2),
            description: selectedItem ? selectedItem.description : ''
        };

        setItems([...items, itemToAdd]);
        setIsAddingItem(false);
        setNewItem({ name: '', price: '', description: '' });
        setError(null);
    };

    const handleCancelAddItem = () => {
        setIsAddingItem(false);
        setNewItem({ name: '', price: '', description: '' });
        setError(null);
    };

    const handleEdit = (id) => {
        const itemToEdit = items.find(item => item.id === id);
        setEditingId(id);
        setEditData({ ...itemToEdit });
    };

    const handleSave = (id) => {
        if (!editData.price || isNaN(editData.price) || parseFloat(editData.price) <= 0) {
            setError('Please enter a valid price');
            return;
        }

        setItems(items.map(item =>
            item.id === id ? {
                ...editData,
                price: parseFloat(editData.price).toFixed(2)
            } : item
        ));
        setEditingId(null);
        setError(null);
    };

    const handleCancel = () => {
        setEditingId(null);
        setError(null);
    };

    const handleDelete = (id) => {
        setItems(items.filter(item => item.id !== id));
    };

    const handleEditChange = (field, value) => {
        setEditData(prev => ({ ...prev, [field]: value }));
    };

    const handleSavePriceList = async () => {
        // Clear previous errors
        setError(null);
        setIsLoading(true);

        try {
            // Transform items to match backend expectations
            const transformedItems = items.map(item => ({
                item_id: item.name,  // Send the item ID stored in the name field
                price: parseFloat(item.price),
                item_description: item.description || '',
                unit_of_measure: 'Each' // Add if your backend expects this
            }));

            // Prepare payload with backend-compatible field names
            const payload = {
                price_list_name: formData.priceListName.trim(),
                status: formData.priceListStatus,
                category: formData.category,
                description: formData.description?.trim() || null,
                created_by: formData.createdBy,
                approved_by: formData.approvedBy,
                creation_date: formData.creationDate || new Date().toISOString(),
                items: transformedItems
            };

            const url = formData.id
                ? `${process.env.REACT_APP_API_URL}/price-lists/${formData.id}`
                : `${process.env.REACT_APP_API_URL}/price-lists`;

            const method = formData.id ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to save price list');
            }

            // Refresh price lists after successful save
            const refreshResponse = await fetch(`${process.env.REACT_APP_API_URL}/price-lists`);
            if (!refreshResponse.ok) {
                throw new Error('Failed to fetch updated price lists');
            }
            const updatedPriceLists = await refreshResponse.json();
            setSavedPriceLists(updatedPriceLists);

            // Show success feedback
            alert(formData.id
                ? 'Price list updated successfully'
                : 'Price list created successfully');

            if (!formData.id) {
                resetForm();
            }

        } catch (err) {
            console.error('Save operation failed:', err);
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };
    const validateInputs = () => {
        const errors = [];

        if (!formData.priceListName.trim()) {
            errors.push('Price list name is required');
        }

        if (items.length === 0) {
            errors.push('At least one item is required');
        }

        // Check for duplicate items
        const itemIds = items.map(item => item.name);
        const hasDuplicates = new Set(itemIds).size !== itemIds.length;
        if (hasDuplicates) {
            errors.push('Duplicate items found in the list');
        }

        // Validate each item's price
        const invalidPrices = items.filter(item =>
            isNaN(item.price) || parseFloat(item.price) <= 0
        );
        if (invalidPrices.length > 0) {
            errors.push('All items must have valid positive prices');
        }

        return errors.length > 0 ? errors.join(' and ') : null;
    };

    const resetForm = () => {
        setFormData({
            id: null,
            priceListName: '',
            priceListStatus: 'active',
            createdBy: 'Admin User',
            approvedBy: 'Finance Team',
            creationDate: new Date().toISOString().split('T')[0],
            description: ''
        });
        setItems([]);
        setError(null);
    };

    const handleViewPriceList = async (id) => {
        setIsLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/price-lists/${id}`);
            if (!response.ok) {
                throw new Error('Failed to fetch price list');
            }
            const priceList = await response.json();

            // Ensure we have the service items before proceeding
            if (ITEM_OPTIONS.length === 0) {
                await new Promise(resolve => {
                    const checkOptions = () => {
                        if (ITEM_OPTIONS.length > 0) {
                            resolve();
                        } else {
                            setTimeout(checkOptions, 100);
                        }
                    };
                    checkOptions();
                });
            }

            const formattedDate = priceList.creation_date
                ? new Date(priceList.creation_date).toISOString().split('T')[0]
                : new Date().toISOString().split('T')[0];

            setFormData({
                id: priceList.id,
                priceListName: priceList.name,
                priceListStatus: priceList.status,
                category: priceList.category || '',
                createdBy: priceList.created_by,
                approvedBy: priceList.approved_by,
                creationDate: formattedDate,
                description: priceList.description
            });

            if (priceList.items && Array.isArray(priceList.items)) {
                // The key fix: properly handle item IDs and names
                const transformedItems = priceList.items.map(item => {
                    // Try to find the matching item in ITEM_OPTIONS
                    const matchedItem = ITEM_OPTIONS.find(option =>
                        option.value?.toString() === item.item_name?.toString() ||
                        option.label?.toString() === item.item_name?.toString()
                    );

                    return {
                        id: Date.now() + Math.random(),
                        name: matchedItem ? matchedItem.value : item.item_name, // Store the ID
                        price: item.price?.toString() || '',
                        description: item.item_description || ''
                    };
                });

                setItems(transformedItems);
            } else {
                setItems([]);
            }

        } catch (err) {
            setError(err.message);
            console.error('Error fetching price list:', err);
        } finally {
            setIsLoading(false);
        }
    };


    const handleDeletePriceList = async (id) => {
        if (!window.confirm('Are you sure you want to delete this price list? This action cannot be undone.')) {
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/price-lists/${id}`, {
                method: 'DELETE'
            });

            if (!response.ok) {
                throw new Error('Failed to delete price list');
            }

            setSavedPriceLists(savedPriceLists.filter(list => list.id !== id));

            if (formData.id === id) {
                resetForm();
            }

            if (expandedPriceList === id) {
                setExpandedPriceList(null);
            }

        } catch (err) {
            setError(err.message);
            console.error('Error deleting price list:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const toggleExpandPriceList = (id) => {
        setExpandedPriceList(expandedPriceList === id ? null : id);
    };

    return (
        <Box sx={{ p: 3, backgroundColor:"#f8fafc", minHeight: '100vh' }}>
            {isLoading && (
                <Box sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999
                }}>
                    <CircularProgress color="primary" size={60} />
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Grid container spacing={3} sx={{ marginBottom: "15px" }}>
                <Grid item xs={12} md={12}>
                    <StyledCard>
                        <CardHeader
                            title={
                                <Box display="flex" alignItems="center">
                                    <DescriptionIcon color="primary" sx={{ mr: 1 }} />
                                    <Typography variant="h6" fontWeight="600">
                                        {formData.id ? 'Edit Price List' : 'Create New Price List'}
                                    </Typography>
                                </Box>
                            }
                            sx={{
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                bgcolor: alpha(theme.palette.primary.light, 0.03),
                                p: 2
                            }}
                        />
                        <CardContent sx={{ p: 3 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6} sx={{ width: "30%" }}>
                                    <TextField
                                        fullWidth
                                        label="Price List Name"
                                        name="priceListName"
                                        value={formData.priceListName}
                                        onChange={handleChange}
                                        variant="outlined"
                                        size="medium"
                                        required
                                        InputProps={{
                                            startAdornment: <DescriptionIcon color="action" sx={{ mr: 1 }} />
                                        }}
                                    />
                                </Grid>
                                {/* Category Dropdown */}
                                <Grid item xs={12} sm={6} sx={{ width: "30%" }}>
                                    <TextField
                                        select
                                        label="Category *"
                                        name="category"
                                        value={formData.category || ''}
                                        onChange={handleChange}
                                        fullWidth
                                        required
                                        size="medium"
                                        variant="outlined"
                                        InputProps={{
                                            sx: {
                                                height: '56px',
                                                display: 'flex',
                                                alignItems: 'center'
                                            }
                                        }}
                                        SelectProps={{
                                            renderValue: (selected) => (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {CATEGORY_OPTIONS.find(opt => opt.value === selected)?.icon}
                                                    <Typography variant="body1">
                                                        {CATEGORY_OPTIONS.find(opt => opt.value === selected)?.label || 'Select a category'}
                                                    </Typography>
                                                </Box>
                                            )
                                        }}
                                    >   
                                        {CATEGORY_OPTIONS.map(option => (
                                            <MenuItem
                                                key={option.value}
                                                value={option.value}
                                                sx={{
                                                    minHeight: '48px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 2
                                                }}
                                            >
                                                {option.icon}
                                                <Typography variant="body1">
                                                    {option.label}
                                                </Typography>
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>

                                <Grid item xs={12} sm={6} sx={{ width: "30%" }}>
                                    <FormControl fullWidth size="medium">
                                        <TextField
                                            select
                                            label="Status"
                                            name="priceListStatus"
                                            value={formData.priceListStatus}
                                            onChange={handleChange}
                                            variant="outlined"
                                            required
                                            InputProps={{
                                                startAdornment: <ApprovedIcon color="action" sx={{ mr: 1 }} />
                                            }}
                                        >
                                            {PRICE_LIST_STATUS.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sx={{ width: "30%" }}>
                                    <TextField
                                        fullWidth
                                        label="Description"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        variant="outlined"
                                        size="small"
                                        multiline
                                        rows={2}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} sx={{ width: "30%" }}>
                                    <TextField
                                        fullWidth
                                        label="Created By"
                                        name="createdBy"
                                        value={formData.createdBy}
                                        onChange={handleChange}
                                        variant="outlined"
                                        size="medium"
                                        required
                                        InputProps={{
                                            startAdornment: <PersonIcon color="action" sx={{ mr: 1 }} />
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} sx={{ width: "30%" }}>
                                    <TextField
                                        fullWidth
                                        label="Approved By"
                                        name="approvedBy"
                                        value={formData.approvedBy}
                                        onChange={handleChange}
                                        variant="outlined"
                                        size="medium"
                                        required
                                        InputProps={{
                                            startAdornment: <ApprovedIcon color="action" sx={{ mr: 1 }} />
                                        }}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6} sx={{ width: "30%" }}>
                                    <TextField
                                        fullWidth
                                        label="Creation Date"
                                        name="creationDate"
                                        type="date"
                                        value={formData.creationDate}
                                        onChange={handleChange}
                                        variant="outlined"
                                        size="medium"
                                        required
                                        InputLabelProps={{ shrink: true }}
                                        InputProps={{
                                            startAdornment: <DateIcon color="action" sx={{ mr: 1 }} />
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </StyledCard>
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                <Grid item xs={12} md={8} sx={{ width: "60%" }}>
                    <StyledCard>
                        <CardHeader
                            title={
                                <Box display="flex" alignItems="center">
                                    <CategoryIcon color="primary" sx={{ mr: 1 }} />
                                    <Typography variant="h6" fontWeight="600">
                                        Line Items
                                    </Typography>
                                </Box>
                            }
                            action={
                                <Box display="flex" alignItems="center" gap={1}>
                                    {!isAddingItem && (
                                        <StyledButton
                                            variant="contained"
                                            startIcon={<AddIcon />}
                                            onClick={handleAddItemClick}
                                            size="small"
                                        >
                                            Add Item
                                        </StyledButton>
                                    )}
                                    <StyledButton
                                        variant="contained"
                                        color="primary"
                                        size="medium"
                                        onClick={handleSavePriceList}
                                        disabled={items.length === 0 || !formData.priceListName}
                                        startIcon={<SaveIcon />}
                                        sx={{ minWidth: 180 }}
                                    >
                                        {formData.id ? 'Update Price List' : 'Save Price List'}
                                    </StyledButton>
                                </Box>
                            }
                            sx={{
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                bgcolor: alpha(theme.palette.primary.light, 0.03),
                                p: 2
                            }}
                        />
                        <CardContent sx={{ p: 0 }}>
                            <StyledTableContainer component={Paper} elevation={0}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell width="50%">Item</TableCell>
                                            <TableCell width="30%">Price ($)</TableCell>
                                            <TableCell width="20%">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {items.length === 0 && !isAddingItem ? (
                                            <TableRow>
                                                <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', opacity: 0.7 }}>
                                                        <AddIcon sx={{ fontSize: 36, color: theme.palette.text.secondary, mb: 1 }} />
                                                        <Typography variant="body2" color="text.secondary">
                                                            No items added yet
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            items.map((item) => (
                                                <TableRow key={item.id} hover>
                                                    <TableCell>
                                                        {editingId === item.id ? (
                                                            <FormControl fullWidth size="small">
                                                                <Select
                                                                    name="name"
                                                                    value={editData?.name || ''}
                                                                    onChange={(e) => handleEditChange('name', e.target.value)}
                                                                    displayEmpty
                                                                    required
                                                                >
                                                                    {[
                                                                        // Include the currently selected item even if it's not in available items
                                                                        ...(editData?.name ? [ITEM_OPTIONS.find(opt =>
                                                                            opt.value?.toString() === editData.name?.toString() ||
                                                                            opt.label?.toString() === editData.name?.toString()
                                                                        )].filter(Boolean) : []),
                                                                        ...getAvailableItems()
                                                                    ]
                                                                        .filter((item, index, self) =>
                                                                            item && index === self.findIndex(t =>
                                                                                t?.value === item?.value
                                                                            )
                                                                        )
                                                                        .map((option) => (
                                                                            <MenuItem key={option.value} value={option.value}>
                                                                                {option.label}
                                                                            </MenuItem>
                                                                        ))}
                                                                </Select>
                                                            </FormControl>
                                                        ) : (
                                                            <Box display="flex" alignItems="center">
                                                                <CategoryIcon color="action" sx={{ mr: 1, fontSize: 18 }} />
                                                                <Box>
                                                                    <Typography>{getItemNameById(item.name)}</Typography>
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {item.description || getItemDescriptionById(item.name)}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {editingId === item.id ? (
                                                            <TextField
                                                                type="number"
                                                                name="price"
                                                                value={editData.price}
                                                                onChange={(e) => handleEditChange('price', e.target.value)}
                                                                size="small"
                                                                fullWidth
                                                                required
                                                                inputProps={{
                                                                    step: "0.01",
                                                                    min: "0.01"
                                                                }}
                                                            />
                                                        ) : (
                                                            <Box display="flex" alignItems="center">

                                                                <PriceIcon color="action" sx={{ mr: 1 }} />

                                                                {parseFloat(item.price).toFixed(2)}
                                                            </Box>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {editingId === item.id ? (
                                                            <Box sx={{ display: 'flex' }}>
                                                                <Tooltip title="Save">
                                                                    <ActionIconButton
                                                                        size="small"
                                                                        onClick={() => handleSave(item.id)}
                                                                        color="primary"
                                                                    >
                                                                        <SaveIcon fontSize="small" />
                                                                    </ActionIconButton>
                                                                </Tooltip>
                                                                <Tooltip title="Cancel">
                                                                    <ActionIconButton
                                                                        size="small"
                                                                        onClick={handleCancel}
                                                                        color="error"
                                                                    >
                                                                        <CancelIcon fontSize="small" />
                                                                    </ActionIconButton>
                                                                </Tooltip>
                                                            </Box>
                                                        ) : (
                                                            <Box sx={{ display: 'flex' }}>
                                                                <Tooltip title="Edit">
                                                                    <ActionIconButton
                                                                        size="small"
                                                                        onClick={() => handleEdit(item.id)}
                                                                        color="primary"
                                                                    >
                                                                        <EditIcon fontSize="small" />
                                                                    </ActionIconButton>
                                                                </Tooltip>
                                                                <Tooltip title="Delete">
                                                                    <ActionIconButton
                                                                        size="small"
                                                                        onClick={() => handleDelete(item.id)}
                                                                        color="error"
                                                                    >
                                                                        <DeleteIcon fontSize="small" />
                                                                    </ActionIconButton>
                                                                </Tooltip>
                                                            </Box>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}

                                        {isAddingItem && (
                                            <TableRow sx={{ bgcolor: alpha(theme.palette.primary.light, 0.02) }}>
                                                <TableCell>
                                                    <FormControl fullWidth size="small">
                                                        <Select
                                                            name="name"
                                                            value={newItem.name}
                                                            onChange={handleNewItemChange}
                                                            displayEmpty
                                                            required
                                                        >
                                                            {isItemsLoading ? (
                                                                <MenuItem disabled>Loading items...</MenuItem>
                                                            ) : getAvailableItems().length === 0 ? (
                                                                <MenuItem disabled>No items available</MenuItem>
                                                            ) : (
                                                                [
                                                                    <MenuItem key="placeholder" value="" disabled>Select item</MenuItem>,
                                                                    ...getAvailableItems().map((option) => (
                                                                        <MenuItem
                                                                            key={option.value}
                                                                            value={option.value}
                                                                        >
                                                                            {option.label}
                                                                        </MenuItem>
                                                                    ))
                                                                ]
                                                            )}
                                                        </Select>
                                                    </FormControl>
                                                </TableCell>
                                                <TableCell>
                                                    <TextField
                                                        type="number"
                                                        name="price"
                                                        value={newItem.price}
                                                        onChange={handleNewItemChange}
                                                        size="small"
                                                        fullWidth
                                                        required
                                                        inputProps={{
                                                            step: "0.01",
                                                            min: "0.01"
                                                        }}
                                                        placeholder="0.00"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex' }}>
                                                        <Tooltip title="Save">
                                                            <ActionIconButton
                                                                size="small"
                                                                onClick={handleSaveNewItem}
                                                                color="primary"
                                                            >
                                                                <SaveIcon fontSize="small" />
                                                            </ActionIconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Cancel">
                                                            <ActionIconButton
                                                                size="small"
                                                                onClick={handleCancelAddItem}
                                                                color="error"
                                                            >
                                                                <CancelIcon fontSize="small" />
                                                            </ActionIconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </StyledTableContainer>
                        </CardContent>
                    </StyledCard>
                </Grid>

                <Grid item xs={12} md={4} sx={{ width: "38%" }}>
                    <StyledCard>
                        <CardHeader
                            title={
                                <Box display="flex" alignItems="center">
                                    <CartIcon color="primary" sx={{ mr: 1 }} />
                                    <Typography variant="h6" fontWeight="600">
                                        Saved Price Lists
                                    </Typography>
                                </Box>
                            }
                            sx={{
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                bgcolor: alpha(theme.palette.primary.light, 0.03),
                                p: 2
                            }}
                        />
                        <CardContent sx={{ p: 2 }}>
                            {savedPriceLists.length === 0 ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, opacity: 0.7 }}>
                                    <CartIcon sx={{ fontSize: 40, mb: 1, color: 'text.secondary' }} />
                                    <Typography variant="body2" color="text.secondary">
                                        No price lists saved yet
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {savedPriceLists.map((list) => (
                                        <StyledCard key={list.id} sx={{ p: 2 }}>
                                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                                <Box>
                                                    <Typography variant="subtitle1" fontWeight="600">
                                                        {list.name}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                        {list.description}
                                                    </Typography>
                                                </Box>
                                                <Box display="flex" alignItems="center">
                                                    <StatusChip
                                                        label={list.status.charAt(0).toUpperCase() + list.status.slice(1)}
                                                        size="small"
                                                        status={list.status}
                                                    />
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => toggleExpandPriceList(list.id)}
                                                        sx={{ ml: 1 }}
                                                    >
                                                        {expandedPriceList === list.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                    </IconButton>
                                                </Box>
                                            </Box>

                                            <Collapse in={expandedPriceList === list.id}>
                                                <Divider sx={{ my: 2 }} />

                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        <DateIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                                                        Created: {new Date(list.creation_date).toLocaleDateString()}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        <PersonIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                                                        By: {list.created_by}
                                                    </Typography>
                                                </Box>

                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        <ApprovedIcon sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                                                        Approved by: {list.approved_by}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                    Category: {list.category || 'N/A'}
                                                </Typography>
                                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                    Items ({list.item_count || 0})
                                                </Typography>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell>Item Name</TableCell>
                                                            <TableCell align="right">Price</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {list.items && list.items.length > 0 ? (
                                                            list.items.map((item, index) => (
                                                                <TableRow key={`${list.id}-${index}`}>
                                                                    <TableCell>
                                                                        <Box>
                                                                            <Typography variant="body2">
                                                                                {item.item_name || getItemNameById(item.id) || 'Unknown Item'}
                                                                            </Typography>
                                                                            {/* {item.item_description && (
                                                                                <Typography variant="caption" color="text.secondary" display="block">
                                                                                    {item.item_description}
                                                                                </Typography>
                                                                            )} */}
                                                                        </Box>
                                                                    </TableCell>
                                                                    <TableCell align="right">
                                                                        ${parseFloat(item.price).toFixed(2)}
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))
                                                        ) : (
                                                            <TableRow>
                                                                <TableCell colSpan={2} align="center">
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        No items in this price list
                                                                    </Typography>
                                                                </TableCell>
                                                            </TableRow>
                                                        )}
                                                    </TableBody>
                                                </Table>

                                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
                                                    <Tooltip title="Load into Form">
                                                        <ActionIconButton
                                                            size="small"
                                                            color="primary"
                                                            onClick={() => handleViewPriceList(list.id)}
                                                        >
                                                            <VisibilityIcon fontSize="small" />
                                                        </ActionIconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <ActionIconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => handleDeletePriceList(list.id)}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </ActionIconButton>
                                                    </Tooltip>
                                                </Box>
                                            </Collapse>
                                        </StyledCard>
                                    ))}
                                </Box>
                            )}
                        </CardContent>
                    </StyledCard>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Prices;